<script setup lang="ts">
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import MapLabel from './MapLabel.vue';
import {
  computed,
  markRaw,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  watch,
  type PropType,
  type Ref,
} from 'vue';
import i18n from '@/i18n';
import { localizeMapLabels } from '@/libs/map-language';
import { MAP_STYLE_URL, withTilesKey } from '@/libs/map-tiles';
import { useRoute } from 'vue-router';
import { MapFocusType, useAppStore, type LatLong, type MapFocus } from '@/stores/app';
import type { MapLngLat, MapRessourceClicked, MapShape, MapStop, MapVehicle } from '@/@types/map';
import { MapLabelPriority, MapZoomLevel } from '@/@types/map';
import type { Bounds } from '@/stores/gtfs';
import type { Position } from '@capacitor/geolocation';
import {
  MAP_SPRITES,
  VEHICLE_SELECTED_SPRITES,
  VEHICLE_BUS_URL,
  VEHICLE_SPRITES,
  addSvgSprite,
  addVehicleBadgeSprites,
  loadImage,
  vehicleBadgeSpriteName,
} from '@/libs/helpers/map-sprites';

import type { Stop } from '@/@types/gtfs-ressource';
import type { VehiclePlacement } from '@pysae/transit-core/vehicle-placement';
import { useVehicleAnimation } from '@/use/vehicle-animation';
import { StopType } from '@/@types/trip-updates';

const BUS_STOP_TEMPORARY = new URL(`@/assets/img/bus_stop_temporary.png`, import.meta.url).href;
const BUS_STOP_UNSERVED = new URL(`@/assets/img/bus_stop_unserved.png`, import.meta.url).href;

/** The trip view thickens the shape and shrinks the stop glyphs from 20 to 16 px. */
const SHAPE_WIDTH = { default: 4, tripView: 5 };
const SHAPE_BORDER_WIDTH = { default: 6, tripView: 8 };
const STOP_ICON_SCALE = { default: 1, tripView: 0.8 };
/** Space between a pin top and the name card above it. */
const LABEL_GAP = 6;
/** `MapLabel` card metrics, to measure the room a name would take on screen. */
const LABEL_FONT = '600 12px Poppins, Arial, sans-serif';
const LABEL_TEXT_HEIGHT = 12;
const LABEL_PADDING_X = 6;
const LABEL_PADDING_Y = 3;
/** Transparent on an ordinary card, green on the selected one: same size either way. */
const LABEL_BORDER = 2;
/**
 * Breathing room kept around a card, against another card and against the edge
 * of the map: enough that two names read apart, no more — a wider ring drops
 * names that had the room to be written.
 */
const LABEL_MARGIN = 4;
/** Vehicle pill, selected size: a name may not land on a vehicle. */
const VEHICLE_SIZE = 36;
/**
 * Two stops this close bearing the same name are the platforms of one stop —
 * the GTFS of a network without stations has one per direction — and the name
 * is written once. Two namesakes further apart than that are two places.
 */
const SAME_STOP_RADIUS = 200;
/** Meridian metres per degree, enough to tell two platforms from two places. */
const EARTH_METERS_PER_DEGREE = 111_320;

/** A spot a card may take: what to hand `MapLabel`, and the room it would eat. */
interface LabelPlacement {
  box: Box;
  offsetX: number;
  clearance: number;
}

interface StopLabel {
  key: string;
  text: string;
  position: MapLngLat;
  /** Room left above the coordinate, negative once the card hangs under it. */
  clearance: number;
  selected: boolean;
  /** `MapLabelPriority`: the lower, the sooner the name is given its spot. */
  priority: number;
  /** Sideways shift, to shoulder a card off its marker or keep it on screen. */
  offsetX: number;
  /** Drawn as a pin: its name is kept even where it covers a vehicle. */
  pin: boolean;
}

/** Screen-space footprint of a card or of a marker a card has to keep clear of. */
interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const route = useRoute();
const appStore = useAppStore();

const emit = defineEmits<{
  click: [event: MapRessourceClicked];
  zoomLevelChange: [zoomLevel: MapZoomLevel];
  /** The point under the centre pin while `picking`, each time the map comes to rest. */
  pick: [point: LatLong];
}>();

const props = defineProps({
  networkBounds: {
    type: Object as PropType<Bounds>,
    default: null,
  },
  focus: {
    type: Object as PropType<MapFocus>,
    default: null,
  },
  selectedStopId: {
    type: String,
    default: undefined,
  },
  showStopNames: {
    type: Boolean,
    default: false,
  },
  mapOldShape: {
    type: Object as PropType<MapShape>,
    default: undefined,
  },
  shapes: {
    type: Array<MapShape>,
    required: true,
  },
  /** Walks of the journey followed, drawn dotted under the rides. */
  walks: {
    type: Array<MapShape>,
    default: () => [],
  },
  stops: {
    type: Object as PropType<{ [key: string]: MapStop }>,
    required: true,
  },
  tripView: {
    type: Boolean,
    default: false,
  },
  userPosition: {
    type: Object as PropType<LatLong>,
    default: null,
  },
  vehicles: {
    type: Object as PropType<{ [key: string]: MapVehicle }>,
    required: true,
  },
  /** A point is picked: a pin stays at the centre of the map, which moves underneath. */
  picking: {
    type: Boolean,
    default: false,
  },
});

const PICK_PIN_SCALE = 1.4;
const pickPinStyle = {
  width: `${MAP_SPRITES.stopSelected.width * PICK_PIN_SCALE}px`,
  height: `${MAP_SPRITES.stopSelected.height * PICK_PIN_SCALE}px`,
};

/** The point under the pin: the centre of the map as drawn, whatever the camera padding. */
function emitPickedPoint() {
  if (!props.picking || !mapInstance.value) return;
  const container = mapInstance.value.getContainer();
  const { lat, lng } = mapInstance.value.unproject([container.clientWidth / 2, container.clientHeight / 2]);
  emit('pick', { lat, lon: lng });
}

watch(
  () => props.picking,
  () => emitPickedPoint(),
);

// Raw: every MapLibre call, `project` on each frame of a gesture included, would
// otherwise go through a reactive proxy of the map and of its internal state.
const mapInstance = shallowRef<maplibregl.Map>();
// Set once the component is torn down, so the async map init below can bail out
// if it lost the race with unmount (style still loading) and clean up itself.
let disposed = false;
/** Bus pictogram, drawn into each vehicle badge. */
let vehicleBus: HTMLImageElement | undefined;
const zoomLevel = ref<MapZoomLevel>();
const lastClicked = ref<MapRessourceClicked>({});
const mapdiv = ref<HTMLElement>() as Ref<HTMLElement>;

const stopIconScale = computed<number>(() =>
  props.tripView ? STOP_ICON_SCALE.tripView : STOP_ICON_SCALE.default,
);

const sheetInset = computed<number>(() => appStore.mapBottomInset);

/** The selected stop, or its platforms once zoomed in enough to tell them apart. */
const selectedStopIds = computed<Array<string>>(() => {
  if (!props.selectedStopId) return [];
  const stop = props.stops[props.selectedStopId];
  if (zoomLevel.value === MapZoomLevel.STOPS && stop && stop.children.length > 0) return stop.children;
  return [props.selectedStopId];
});

// #region Stop names
const stopLabels = shallowRef<Array<StopLabel>>([]);
let labelFrame: number | undefined;

const labelCandidates = computed<Array<StopLabel>>(() => {
  const candidates: Array<StopLabel> = [];
  if (props.showStopNames) {
    Object.values(props.stops)
      .filter(stop => isShownAtZoomLevel(stop) && !selectedStopIds.value.includes(stop.id))
      .forEach(stop =>
        candidates.push({
          key: `stop-${stop.id}`,
          text: stop.name,
          position: stop.position,
          clearance: stop.terminus
            ? MAP_SPRITES.terminus.glyphHeight + LABEL_GAP
            : (MAP_SPRITES.stop.glyphHeight * stopIconScale.value) / 2 + LABEL_GAP,
          selected: false,
          priority: stop.labelPriority ?? MapLabelPriority.DEFAULT,
          offsetX: 0,
          pin: stop.terminus,
        }),
      );
  }
  selectedStopIds.value.forEach(stopId => {
    const stop = props.stops[stopId];
    if (!stop) return;
    candidates.push({
      key: `selected-${stop.id}`,
      text: stop.name,
      position: stop.position,
      clearance: MAP_SPRITES.stopSelected.glyphHeight + LABEL_GAP,
      selected: true,
      priority: MapLabelPriority.SELECTED,
      offsetX: 0,
      pin: true,
    });
  });
  return candidates;
});

// Ahead of the stop names' watcher: a poll is ingested before they avoid the drawn vehicles.
// Not reactive: a new value on every frame would re-render the component and re-place the stop names.
let vehiclePlacements: ReadonlyMap<string, VehiclePlacement> = new Map();
useVehicleAnimation({
  vehicles: () => props.vehicles,
  render: placements => {
    vehiclePlacements = placements;
    drawVehicles();
  },
});

watch([labelCandidates, sheetInset, () => props.vehicles], () => placeStopLabels());

/**
 * Hand each name the first free spot around its marker, the most important
 * names first, dropping only those left with no spot at all. Zooming in spreads
 * the markers apart, so more names come out on their own, no zoom threshold
 * needed.
 *
 * A name that had its spot is placed before its equals on the next pass:
 * without that, panning would make the names dance from one stop to another.
 */
function placeStopLabels() {
  const map = mapInstance.value;
  if (!map) return;
  // The home map has no name to place: it would still re-render on every frame of a pan.
  if (labelCandidates.value.length === 0) {
    if (stopLabels.value.length > 0) stopLabels.value = [];
    return;
  }
  const shownKeys = new Set(stopLabels.value.map(label => label.key));
  const vehicles = vehicleBoxes(map);
  const placed: Array<Box> = [];
  const shown: Array<StopLabel> = [];
  [...labelCandidates.value]
    .sort((a, b) => a.priority - b.priority || Number(shownKeys.has(b.key)) - Number(shownKeys.has(a.key)))
    .forEach(label => {
      if (shown.some(other => isSameStop(other, label))) return;
      const placement = labelPlacements(map, label).find(
        ({ box }) =>
          !placed.some(other => boxesOverlap(other, box)) &&
          // Vehicles hold their ground against an ordinary name, never against
          // a pin: the ends of a journey are named whatever else is on the map.
          (label.pin || !vehicles.some(vehicle => boxesOverlap(vehicle, box))),
      );
      if (!placement) return;
      placed.push(placement.box);
      shown.push({ ...label, offsetX: placement.offsetX, clearance: placement.clearance });
    });
  // Runs on every frame of a pan, where the cards mostly keep their spot: an equal
  // list is not handed over, or the whole map component would re-render each frame.
  if (!sameLabels(shown, stopLabels.value)) stopLabels.value = shown;
}

function sameLabels(labels: Array<StopLabel>, others: Array<StopLabel>): boolean {
  return (
    labels.length === others.length &&
    labels.every((label, index) => {
      const other = others[index];
      return (
        label.key === other.key &&
        label.text === other.text &&
        label.offsetX === other.offsetX &&
        label.clearance === other.clearance &&
        label.selected === other.selected &&
        label.pin === other.pin &&
        label.position.lng === other.position.lng &&
        label.position.lat === other.position.lat
      );
    })
  );
}

/**
 * The spots the card of `label` may take, each slid sideways to stay on screen,
 * in the order they are worth trying: over the marker first, then shouldered to
 * one side, then under it. A name held to the one spot above its marker was
 * dropped wherever two stops stood close, leaving both markers anonymous.
 *
 * Empty when the marker is off the visible map: a name is worth a spot only
 * where the marker it belongs to can be seen.
 */
function labelPlacements(map: maplibregl.Map, label: StopLabel): Array<LabelPlacement> {
  const point = map.project(label.position);
  const { clientWidth, clientHeight } = map.getContainer();
  if (point.x < 0 || point.x > clientWidth) return [];
  if (point.y < 0 || point.y > clientHeight - sheetInset.value) return [];

  const width = measureLabelWidth(label.text) + (LABEL_PADDING_X + LABEL_BORDER) * 2;
  const height = LABEL_TEXT_HEIGHT + (LABEL_PADDING_Y + LABEL_BORDER) * 2;
  // A pin stands wholly above its coordinate, a disc straddles it: under a disc
  // the card has the same half-glyph to clear as above it.
  const under = -((label.pin ? LABEL_GAP : label.clearance) + height);
  const spots = [
    { dx: 0, clearance: label.clearance },
    { dx: width / 2 + LABEL_GAP, clearance: label.clearance },
    { dx: -(width / 2 + LABEL_GAP), clearance: label.clearance },
    { dx: 0, clearance: under },
  ];

  return spots.flatMap(({ dx, clearance }) => {
    const bottom = point.y - clearance;
    const top = bottom - height;
    if (top < LABEL_MARGIN) return [];
    if (bottom > clientHeight - sheetInset.value - LABEL_MARGIN) return [];

    // Slid back in rather than dropped: the arrival sits at the edge of the map
    // as often as not, and it is the one name that has to stay readable.
    const center = point.x + dx;
    let shift = 0;
    if (center - width / 2 < LABEL_MARGIN) shift = LABEL_MARGIN - (center - width / 2);
    else if (center + width / 2 > clientWidth - LABEL_MARGIN)
      shift = clientWidth - LABEL_MARGIN - (center + width / 2);

    return [
      {
        box: {
          left: center - width / 2 + shift,
          right: center + width / 2 + shift,
          top,
          bottom,
        },
        offsetX: dx + shift,
        clearance,
      },
    ];
  });
}

function vehicleBoxes(map: maplibregl.Map): Array<Box> {
  return Object.values(props.vehicles).map(vehicle => {
    // Where the vehicle is drawn: gliding, it can be tens of metres ahead of its raw position.
    const drawn = vehiclePlacements.get(vehicle.id)?.latlng;
    const point = map.project(drawn ? { lat: drawn[0], lng: drawn[1] } : vehicle.position);
    return {
      left: point.x - VEHICLE_SIZE / 2,
      right: point.x + VEHICLE_SIZE / 2,
      top: point.y - VEHICLE_SIZE / 2,
      bottom: point.y + VEHICLE_SIZE / 2,
    };
  });
}

/** The same name on two markers all but on top of each other: one stop, two platforms. */
function isSameStop(a: StopLabel, b: StopLabel): boolean {
  if (a.text !== b.text) return false;
  const latitude = ((a.position.lat + b.position.lat) / 2) * (Math.PI / 180);
  const north = (a.position.lat - b.position.lat) * EARTH_METERS_PER_DEGREE;
  const east = (a.position.lng - b.position.lng) * EARTH_METERS_PER_DEGREE * Math.cos(latitude);
  return north * north + east * east < SAME_STOP_RADIUS * SAME_STOP_RADIUS;
}

/** Two cards may not touch: they are kept `LABEL_MARGIN` apart. */
function boxesOverlap(a: Box, b: Box): boolean {
  return (
    a.left < b.right + LABEL_MARGIN &&
    b.left < a.right + LABEL_MARGIN &&
    a.top < b.bottom + LABEL_MARGIN &&
    b.top < a.bottom + LABEL_MARGIN
  );
}

const labelWidths = new Map<string, number>();
let measureContext: CanvasRenderingContext2D | null | undefined;

function measureLabelWidth(text: string): number {
  const cached = labelWidths.get(text);
  if (cached !== undefined) return cached;
  if (measureContext === undefined) measureContext = document.createElement('canvas').getContext('2d');
  let width: number;
  if (measureContext) {
    measureContext.font = LABEL_FONT;
    width = measureContext.measureText(text).width;
  } else {
    // No 2D context: over-estimate rather than let two cards overlap.
    width = text.length * LABEL_TEXT_HEIGHT;
  }
  labelWidths.set(text, width);
  return width;
}

// Names are placed in screen space, so the whole placement is redone as soon as
// the camera moves — once per frame at most.
function scheduleLabelPlacement() {
  if (labelFrame !== undefined) return;
  labelFrame = requestAnimationFrame(() => {
    labelFrame = undefined;
    placeStopLabels();
  });
}
// #endregion

watch(
  () => i18n.global.locale,
  locale => {
    if (mapInstance.value) localizeMapLabels(mapInstance.value, locale);
  },
);

watch(
  () => route.name,
  () => {
    // fix map not resizing correcty when switching between routes
    if (mapInstance.value) mapInstance.value.resize();
  },
);

watch([() => props.focus, () => props.networkBounds, sheetInset], ([focus, , inset], [, , previousInset]) =>
  updateFocus({ reframe: inset !== previousInset || Boolean(focus?.reframe) }),
);

// The computed hands out a new array on every stops poll and zoom step, which
// already refresh the source themselves: only react to an actual selection change.
watch(
  () => selectedStopIds.value.join('\n'),
  () => {
    updateSelectedStop();
    updateStopsSource();
  },
);

watch(
  () => props.mapOldShape,
  () => {
    updateOldShapesSource();
  },
);

watch(
  () => props.shapes,
  () => {
    updateShapesSource();
  },
);

watch(
  () => props.walks,
  () => {
    updateWalksSource();
  },
);

// The selection can land before the stops (deep link, reload): redraw its pin
// once they arrive, the selection key alone would not change.
watch(
  () => props.stops,
  () => {
    updateStopsSource();
    updateSelectedStop();
  },
);

watch(
  () => props.tripView,
  () => {
    updateViewSizing();
  },
);

watch(
  () => props.userPosition,
  () => {
    updateUserPositionSource();
  },
);

watch(
  () => props.vehicles,
  () => {
    updateVehiclesSource();
  },
);

watch(zoomLevel, () => {
  updateStopsSource();
  if (zoomLevel.value) emit('zoomLevelChange', zoomLevel.value);
});

onMounted(async () => {
  appStore.setLoading({ map: true });

  const map = new maplibregl.Map({
    container: mapdiv.value,
    style: MAP_STYLE_URL,
    transformRequest: withTilesKey,
    attributionControl: { compact: true },
    touchPitch: false, // Disable pitch with two fingers
    // Animated vehicles update their source every 100 ms; each update re-places the
    // symbols, so the default 300 ms label fade would never settle and keep the map
    // repainting at screen rate (#304).
    fadeDuration: 0,
  });

  await new Promise<void>(resolve => {
    map.on('load', () => {
      resolve();
    });
  });
  localizeMapLabels(map, i18n.global.locale);

  // Unmounted while the style was loading: mapInstance was never published, so
  // the onUnmounted cleanup could not remove this map — do it here.
  if (disposed) {
    map.remove();
    appStore.setLoading({ map: false });
    return;
  }

  // Promise.allSettled is unsupported on iOS Safari 11 (compat/compat lint rule),
  // so catch each image individually instead: one sprite failing to load must
  // not stall the whole map (logged and kept visible, not filtered, per
  // CLAUDE.md — a real load failure here would mean a broken build).
  await Promise.all(
    [
      ...Object.values(MAP_SPRITES).map(sprite => addSvgSprite(map, sprite)),
      addImage(map, BUS_STOP_TEMPORARY, 'bus_stop_temporary'),
      addImage(map, BUS_STOP_UNSERVED, 'bus_stop_unserved'),
      ...[...Object.values(VEHICLE_SPRITES), ...Object.values(VEHICLE_SELECTED_SPRITES)].map(sprite =>
        addSvgSprite(map, sprite),
      ),
      loadImage(VEHICLE_BUS_URL).then(image => {
        vehicleBus = image;
      }),
    ].map(promise => promise.catch(error => console.error('Failed to load map sprite image:', error))),
  );

  // Unmounted while the sprite images were loading: same race as above.
  if (disposed) {
    map.remove();
    appStore.setLoading({ map: false });
    return;
  }

  mapInstance.value = markRaw(map);

  map.resize(); // fix map not resizing correcty - not sure if this is really needed here
  appStore.setLoading({ map: false });

  map.on('zoom', updateZoomLevel);
  map.on('moveend', emitPickedPoint);
  map.on('move', scheduleLabelPlacement);

  const canvas = map.getCanvasContainer();

  map.addSource('oldShape', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'old_shape',
    source: 'oldShape',
    type: 'line',

    paint: {
      'line-width': 3,
      'line-color': '#F99C49',
      'line-dasharray': [0, 2, 2],
      'line-opacity': 0.7,
    },
  });

  map.addSource('shapes', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'shapes_bg',
    source: 'shapes',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-width': shapeBorderWidth(),
      'line-color': '#333333',
    },
  });
  map.addLayer({
    id: 'shapes_hit',
    source: 'shapes',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-width': 14,
      'line-opacity': 0,
    },
  });
  map.addLayer({
    id: 'shapes',
    source: 'shapes',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-width': shapeWidth(),
      'line-color': ['get', 'color'],
    },
  });
  setCursorPointer(map, canvas, 'shapes_hit');

  // Over the rides: a walk to or from a stop runs along the line and must not vanish under it.
  map.addSource('walks', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'walks',
    source: 'walks',
    type: 'line',
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-width': 4,
      'line-color': '#7f847d',
      'line-dasharray': [0, 2],
    },
  });

  map.on('click', 'shapes_hit', e => {
    if (e.features && e.features.length > 0 && lastClicked.value.stopId === undefined) {
      lastClicked.value = {
        routeId: e.features[0].properties!.routeId,
      };
    }
  });

  map.addSource('stops', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'stops',
    source: 'stops',
    type: 'symbol',
    filter: ['all', ['==', 'type', StopType.REGULAR], ['!=', 'terminus', true]],
    layout: {
      'icon-image': MAP_SPRITES.stop.name,
      'icon-size': stopIconScale.value,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  setCursorPointer(map, canvas, 'stops');
  setStopClickAction(map, 'stops');

  map.addLayer({
    id: 'terminus',
    source: 'stops',
    type: 'symbol',
    filter: ['all', ['==', 'type', StopType.REGULAR], ['==', 'terminus', true]],
    layout: {
      'icon-image': MAP_SPRITES.terminus.name,
      'icon-anchor': 'bottom',
      'icon-offset': [0, MAP_SPRITES.terminus.bottomPadding],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  setCursorPointer(map, canvas, 'terminus');
  setStopClickAction(map, 'terminus');

  createUnservedStopLayer(map);
  setCursorPointer(map, canvas, 'unservedStopLayer');
  setStopClickAction(map, 'unservedStopLayer');

  createDeviationStopLayer(map);
  setCursorPointer(map, canvas, 'deviationStopLayer');
  setStopClickAction(map, 'deviationStopLayer');

  map.addSource('selectedStop', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'selectedStop',
    source: 'selectedStop',
    type: 'symbol',
    layout: {
      'icon-image': MAP_SPRITES.stopSelected.name,
      'icon-anchor': 'bottom',
      'icon-offset': [0, MAP_SPRITES.stopSelected.bottomPadding],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  map.addSource('userPosition', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'userPosition',
    source: 'userPosition',
    type: 'symbol',
    layout: {
      'icon-image': MAP_SPRITES.userPosition.name,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });

  createVehicleLayers(map);
  setCursorPointer(map, canvas, 'vehicles_badge');
  // Registered after the stop and shape handlers, so a vehicle wins over what lies under it.
  map.on('click', 'vehicles_badge', e => {
    const vehicleId = e.features?.[0]?.properties?.id;
    if (vehicleId !== undefined) lastClicked.value = { vehicleId };
  });

  // Last: every layer handler above has filled `lastClicked` by now.
  map.on('click', () => {
    emit('click', lastClicked.value);
    lastClicked.value = {};
  });

  updateShapesSource();
  updateOldShapesSource();
  updateWalksSource();
  updateStopsSource();
  updateVehiclesSource();
  updateFocus();
  updateSelectedStop();
  placeStopLabels();
});

function createUnservedStopLayer(map: maplibregl.Map) {
  map.addLayer({
    id: 'unservedStopLayer',
    type: 'symbol',
    source: 'stops',
    filter: ['==', 'type', StopType.CANCELED],
    layout: {
      'icon-image': 'bus_stop_unserved',
      'icon-size': 0.25,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
}
function createDeviationStopLayer(map: maplibregl.Map) {
  map.addLayer({
    id: 'deviationStopLayer',
    type: 'symbol',
    source: 'stops',
    filter: [
      'any',
      ['==', 'type', StopType.AD_HOC],
      ['==', 'type', StopType.REUSE],
      ['==', 'type', StopType.SHIFT],
    ],
    layout: {
      'icon-image': 'bus_stop_temporary',
      'icon-size': 0.25,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
}

/** Stacked from the bottom: heading arrows, shadows, then the badges, one flat image each. */
function createVehicleLayers(map: maplibregl.Map) {
  map.addSource('vehicles', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  const layout = {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    // The selected vehicle is drawn over its neighbours.
    'symbol-sort-key': ['case', ['get', 'selected'], 1, 0],
  } satisfies maplibregl.SymbolLayerSpecification['layout'];
  const paint = {
    'icon-opacity': ['case', ['get', 'stale'], 0.4, 1],
  } satisfies maplibregl.SymbolLayerSpecification['paint'];
  map.addLayer({
    id: 'vehicles_arrow',
    source: 'vehicles',
    type: 'symbol',
    // An unknown heading draws no arrow rather than a wrong one.
    filter: ['has', 'bearing'],
    paint,
    layout: {
      ...layout,
      'icon-image': vehicleIcon('arrow'),
      'icon-rotate': ['get', 'bearing'],
      // A compass heading: it turns with the map, but stays upright like the
      // pill it rides on when the map is tilted.
      'icon-rotation-alignment': 'map',
      'icon-pitch-alignment': 'viewport',
    },
  });
  map.addLayer({
    id: 'vehicles_shadow',
    source: 'vehicles',
    type: 'symbol',
    paint,
    layout: { ...layout, 'icon-image': vehicleIcon('shadow') },
  });
  map.addLayer({
    id: 'vehicles_badge',
    source: 'vehicles',
    type: 'symbol',
    paint,
    layout: { ...layout, 'icon-image': ['get', 'badgeImage'] },
  });
}

function vehicleIcon(part: keyof typeof VEHICLE_SPRITES): maplibregl.ExpressionSpecification {
  return ['case', ['get', 'selected'], VEHICLE_SELECTED_SPRITES[part].name, VEHICLE_SPRITES[part].name];
}

function setCursorPointer(map: maplibregl.Map, canvas: HTMLElement, layerId: string) {
  map.on('mouseenter', layerId, () => {
    canvas.style.cursor = 'pointer';
  });
  map.on('mouseleave', layerId, () => {
    canvas.style.cursor = '';
  });
}

function setStopClickAction(map: maplibregl.Map, layerId: string) {
  map.on('click', layerId, e => {
    if (e.features && e.features?.length > 0) {
      // Only case where stop is not clickable : creation since it does not exist in GTFS and is not handled to be redirected to "stop detail"
      if (e.features[0].properties!.type !== StopType.AD_HOC)
        lastClicked.value = {
          stopId: e.features[0].properties!.stopId,
        };
    }
  });
}

/**
 * Load a bitmap sprite and register it on the map. Must be awaited before adding
 * any layer using `imageName` as `icon-image`, otherwise the layer can render
 * before the sprite is registered (MapLibre logs "Image ... could not be loaded").
 */
async function addImage(map: maplibregl.Map, imagePath: string, imageName: string): Promise<void> {
  const { data } = await map.loadImage(imagePath);
  map.addImage(imageName, data);
}

// Visible to the passenger: on the canvas and not under the sheet.
function isVisible(pos: MapLngLat): boolean {
  if (!mapInstance.value) return false;
  const point = mapInstance.value.project(pos);
  const { clientWidth, clientHeight } = mapInstance.value.getContainer();
  return point.x >= 0 && point.x <= clientWidth && point.y >= 0 && point.y <= clientHeight - sheetInset.value;
}
function shapeWidth(): number {
  return props.tripView ? SHAPE_WIDTH.tripView : SHAPE_WIDTH.default;
}
function shapeBorderWidth(): number {
  return props.tripView ? SHAPE_BORDER_WIDTH.tripView : SHAPE_BORDER_WIDTH.default;
}

function isShownAtZoomLevel(stop: MapStop): boolean {
  // filter displayed stops by zoom level, or display everything if zoomLevel is stops
  return (
    Boolean(zoomLevel.value && stop.zoomLevel[zoomLevel.value]) || zoomLevel.value === MapZoomLevel.STOPS
  );
}

// #region Focus
// Below this much visible map the sheet has all but covered it: reframing the
// strip left behind the buttons is wasted motion, and MapLibre cannot fit bounds
// into it anyway. The last framing stays until the sheet comes back down.
const MIN_VISIBLE_MAP_HEIGHT = 120;
const FOCUS_MARGIN = 20;

// The sheet goes into the camera padding, which MapLibre keeps from one camera
// move to the next: every framing centres on the part the passenger can see.
function focusPadding(): maplibregl.PaddingOptions {
  // While a point is picked, a framing must land under the centre pin.
  if (props.picking) return { top: 0, left: 0, right: 0, bottom: 0 };
  return { top: FOCUS_MARGIN, left: FOCUS_MARGIN, right: 70, bottom: sheetInset.value + FOCUS_MARGIN };
}

/** `reframe`: the visible area changed (the sheet snapped), so re-centre even what is already on screen. */
function updateFocus({ reframe = false }: { reframe?: boolean } = {}) {
  const focus = props.focus || {
    type: MapFocusType.BOUNDS,
    elem: props.networkBounds,
    noAnimation: true,
  };
  if (!mapInstance.value) return;
  if (mapInstance.value.getContainer().clientHeight - sheetInset.value < MIN_VISIBLE_MAP_HEIGHT) return;
  const padding = focusPadding();
  let options: maplibregl.EaseToOptions;
  switch (focus.type) {
    case MapFocusType.BOUNDS:
      const elemBounds = focus.elem as Bounds;
      if (elemBounds && (elemBounds.north || elemBounds.south)) {
        options = {
          padding,
          duration: 500,
          animate: true,
        };
        if (focus.noAnimation) options.animate = false;
        mapInstance.value.fitBounds(
          [
            elemBounds.west as number,
            elemBounds.south as number,
            elemBounds.east as number,
            elemBounds.north as number,
          ],
          options,
        );
      }
      break;

    case MapFocusType.GEOLOCATION:
      const elemCoords = focus.elem as Position['coords'];
      options = {
        center: {
          lat: elemCoords.latitude,
          lng: elemCoords.longitude,
        },
        padding,
      };
      if (mapInstance.value.getZoom() < 16) options.zoom = 17;
      mapInstance.value.flyTo(options);
      break;

    case MapFocusType.STOP:
    case MapFocusType.STATION:
      const elemStop = focus.elem as Stop;
      options = {
        center: {
          lng: elemStop.stop_lon,
          lat: elemStop.stop_lat,
        },
        padding,
      };
      if (!reframe && isVisible(options.center as MapLngLat) && mapInstance.value.getZoom() >= 16) break;
      if (mapInstance.value.getZoom() < 16) options.zoom = 17;
      options.duration = 500;
      options.animate = !focus.noAnimation;
      mapInstance.value.easeTo(options);
      break;

    default:
      break;
  }
}
// #endregion

function updateSelectedStop() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('selectedStop') as maplibregl.GeoJSONSource;
  if (!source) return;

  source.setData({
    type: 'FeatureCollection',
    features: selectedStopIds.value.flatMap(stopId => {
      const stop = props.stops[stopId];
      if (!stop) return [];
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [stop.position.lng, stop.position.lat],
        },
        properties: {},
      };
    }),
  });
}

function updateShapesSource() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('shapes') as maplibregl.GeoJSONSource;
  if (!source) return;
  const geoJson: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
    type: 'FeatureCollection',
    features: Object.values(props.shapes) as any as GeoJSON.Feature<GeoJSON.LineString>[],
  };
  source.setData(geoJson);
}

function updateWalksSource() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('walks') as maplibregl.GeoJSONSource;
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: props.walks.map(walk => ({
      type: 'Feature',
      geometry: walk.geometry,
      properties: walk.properties,
    })),
  } as GeoJSON.FeatureCollection<GeoJSON.LineString>);
}

function updateOldShapesSource() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('oldShape') as maplibregl.GeoJSONSource;
  if (!source) return;
  const geoJson: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
    type: 'FeatureCollection',
    features: (props.mapOldShape ? [props.mapOldShape] : []) as any as GeoJSON.Feature<GeoJSON.LineString>[],
  };
  source.setData(geoJson);
}

function updateStopsSource() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('stops') as maplibregl.GeoJSONSource;
  if (!source) return;

  // The selected stop is drawn by the `selectedStop` layer alone: its disc would show under the pin.
  source.setData({
    type: 'FeatureCollection',
    features: Object.values(props.stops)
      .filter(stop => isShownAtZoomLevel(stop) && !selectedStopIds.value.includes(stop.id))
      .map(stop => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [stop.position.lng, stop.position.lat],
        },
        properties: {
          stopId: stop.id,
          type: stop.type,
          terminus: stop.terminus,
        },
      })),
  });
}

function updateVehiclesSource() {
  const map = mapInstance.value;
  if (!map?.getSource('vehicles')) return;
  Object.values(props.vehicles).forEach(vehicle => addVehicleBadgeSprites(map, vehicle.color, vehicleBus));
  drawVehicles();
}

/** Source data only: run on every animation frame, it rebuilds neither layers nor sprites. */
function drawVehicles() {
  const source = mapInstance.value?.getSource('vehicles') as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: Object.values(props.vehicles).map(vehicleFeature),
  });
}

function vehicleFeature(vehicle: MapVehicle): GeoJSON.Feature<GeoJSON.Point> {
  const placement = vehiclePlacements.get(vehicle.id);
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: placement
        ? [placement.latlng[1], placement.latlng[0]]
        : [vehicle.position.lng, vehicle.position.lat],
    },
    properties: {
      id: vehicle.id,
      // Left out rather than undefined, which would survive the clone to the worker and pass `has`.
      ...((placement?.bearing ?? vehicle.bearing) !== undefined && {
        bearing: placement?.bearing ?? vehicle.bearing,
      }),
      badgeImage: vehicleBadgeSpriteName(vehicle.color, vehicle.selected),
      selected: vehicle.selected,
      stale: vehicle.stale ?? false,
    },
  };
}

function updateViewSizing() {
  if (!mapInstance.value) return;
  mapInstance.value.setPaintProperty('shapes', 'line-width', shapeWidth());
  mapInstance.value.setPaintProperty('shapes_bg', 'line-width', shapeBorderWidth());
  mapInstance.value.setLayoutProperty('stops', 'icon-size', stopIconScale.value);
}

function updateUserPositionSource() {
  if (!mapInstance.value) return;
  const source = mapInstance.value.getSource('userPosition') as maplibregl.GeoJSONSource;
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [props.userPosition.lon, props.userPosition.lat],
        },
        properties: {},
      },
    ],
  });
}

onUnmounted(() => {
  disposed = true;
  if (labelFrame !== undefined) cancelAnimationFrame(labelFrame);
  mapInstance.value?.remove();
  mapInstance.value = undefined;
});

function updateZoomLevel() {
  if (mapInstance.value) {
    const zoom = mapInstance.value.getZoom();
    if (zoom >= 16) zoomLevel.value = MapZoomLevel.STOPS;
    else if (zoom >= 12) zoomLevel.value = MapZoomLevel.STATIONS;
    else zoomLevel.value = MapZoomLevel.TERMINUS;
  }
}
</script>

<template>
  <div>
    <!-- MapLibre expects an empty container: the name card wrappers stay outside it. -->
    <div id="mapdiv" ref="mapdiv" />
    <div v-if="picking" class="map-pick-pin" aria-hidden="true">
      <img :src="MAP_SPRITES.stopSelected.url" alt="" class="map-pick-pin__pin" :style="pickPinStyle" />
    </div>
    <template v-if="mapInstance">
      <!-- The marker moves the card out of this div; Vue only ever reorders the
      wrapper it kept, never a node MapLibre took away. -->
      <div v-for="label in stopLabels" :key="label.key">
        <MapLabel
          :map-instance="mapInstance"
          :position="label.position"
          :text="label.text"
          :clearance="label.clearance"
          :offset-x="label.offsetX"
          :selected="label.selected"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
#mapdiv,
.map-pick-pin {
  position: fixed;
  top: 0;
  bottom: $navbar-height;
  width: 100%;
}

.map-pick-pin {
  pointer-events: none;
  z-index: 1;

  &__pin {
    left: 50%;
    position: absolute;
    top: 50%;
    // The pin's tip, its bottom edge, marks the point.
    transform: translate(-50%, -100%);
  }
}
</style>
